/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-type is a library for typechecking of props
import PropTypes from "prop-types";
import * as React from "react";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// NewUser page components
import FormField from "layouts/pages/users/new-user/components/FormField";

import { API_ESA_OFFICE } from "utils";
function InfoUser({ formData }) {
  const { formField, values, errors, touched, setValues } = formData;
  const {
    nama,
    instansi,
    pangkat,
    jabatan,
    bidang,
    unit,
    uid_RSS,
    avatar,
    status,
  } = formField;
  const {
    nama: namaV,
    instansi: instansiV,
    pangkat: pangkatV,
    jabatan: jabatanV,
    bidang: bidangV,
    unit: unitV,
    level_jabatan: levelJabatanV,
    uid_RSS: uid_RSSV,
    avatar: avatarV,
    status: statusV,
  } = values;

  const [stateValue, setStateValue] = React.useState("");
  const [dinasStruktur, setDinasStruktur] = React.useState(null);
  const [loadingStruktur, setLoadingStruktur] = React.useState(true);
  const [listDinas, setListDinas] = React.useState([]);
  const [loadingDinas, setLoadingDinas] = React.useState(true);
  const [selectedDinas, setSelectedDinas] = React.useState(null);

  // Fungsi untuk fetching struktur dinas
  const fetchStruktur = React.useCallback(async (dinasId) => {
    if (!dinasId) return;

    setLoadingStruktur(true);
    try {
      const res = await axios.get(`${API_ESA_OFFICE}dinas/${dinasId}/struktur`);
      setDinasStruktur(res.data && res.data[0] ? res.data[0] : null);
    } catch (err) {
      setDinasStruktur(null);
    }
    setLoadingStruktur(false);
  }, []);

  // Effect untuk loading awal - fetch list dinas dan set default
  React.useEffect(() => {
    const initializeData = async () => {
      // Ambil user dari localStorage dan set instansi jika ada
      const userStorage = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user"))
        : null;

      const defaultDinasId =
        userStorage && userStorage.dinas_id ? userStorage.dinas_id : 33;

      // Fetch list dinas
      setLoadingDinas(true);
      try {
        const res = await axios.get(`${API_ESA_OFFICE}dinas`);
        const dinasData = res.data || [];
        setListDinas(dinasData);

        // Set default dinas berdasarkan dinasId
        const defaultDinas = dinasData.find(
          (d) => d.dinas_id === defaultDinasId
        );
        if (defaultDinas) {
          setSelectedDinas(defaultDinas);
          // Update form values untuk default dinas
          setValues((prevValues) => ({
            ...prevValues,
            instansi: defaultDinas.nama_dinas,
            dinas_id: defaultDinas.dinas_id,
          }));
          // Trigger fetchStruktur untuk dinas default
          fetchStruktur(defaultDinasId);
        }
      } catch (err) {
        console.error("Error fetching list dinas:", err);
        setListDinas([]);
      }
      setLoadingDinas(false);
    };

    initializeData();
  }, []); // Hanya jalankan sekali saat mount

  // Effect untuk menangani perubahan selectedDinas (hanya saat user mengubah pilihan)
  React.useEffect(() => {
    if (selectedDinas && selectedDinas.dinas_id) {
      // Trigger fetchStruktur saat dinas berubah
      fetchStruktur(selectedDinas.dinas_id);

      // Update form values
      setValues((prevValues) => ({
        ...prevValues,
        instansi: selectedDinas.nama_dinas,
        dinas_id: selectedDinas.dinas_id,
      }));
    }
  }, [selectedDinas, fetchStruktur, setValues]);

  React.useEffect(() => {
    setValues((prevValues) => ({
      ...prevValues,
      state: stateValue,
      uid_RSS: uid_RSSV,
      level_jabatan: levelJabatanV,
      dinas_id:
        dinasStruktur && dinasStruktur.dinas_id ? dinasStruktur.dinas_id : "",
    }));
  }, [stateValue, dinasStruktur, setValues]);

  // Ambil opsi jabatan dari dinasStruktur jika ada
  const jabatanOptions =
    dinasStruktur && Array.isArray(dinasStruktur.struktur)
      ? [
          { value: "", label: "Pilih Jabatan" },
          ...dinasStruktur.struktur.map((j) => ({
            value: j.nama_jabatan,
            label: j.nama_jabatan,
          })),
        ]
      : [];

  // Ambil opsi bidang dari dinasStruktur jika ada
  const bidangOptions =
    dinasStruktur && Array.isArray(dinasStruktur.bidang)
      ? [
          { value: "", label: "Pilih Bidang" },
          ...dinasStruktur.bidang.map((b) => ({
            value: b.bidang_id,
            label: b.bidang,
          })),
        ]
      : [];

  return (
    <MDBox>
      <MDTypography variant="h5" fontWeight="bold">
        Informasi User
      </MDTypography>
      <MDBox mt={1.625}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormField
              type={nama.type}
              label={nama.label}
              name={nama.name}
              value={namaV}
              placeholder={nama.placeholder}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              type={status.type}
              label={status.label}
              name={status.name}
              value={statusV}
              options={[
                { value: "", label: "Pilih Status" },
                { value: "user", label: "User" },
                { value: "admin", label: "Admin" },
              ]}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MDBox mt={-1.625}>
              <MDBox mb={1} ml={0.5}>
                <MDTypography
                  component="label"
                  variant="caption"
                  fontWeight="regular"
                  color="text"
                >
                  {instansi.label}
                </MDTypography>
              </MDBox>
              <Autocomplete
                value={selectedDinas}
                onChange={(event, newValue) => {
                  setSelectedDinas(newValue);
                }}
                options={listDinas}
                getOptionLabel={(option) => option.nama_dinas || ""}
                isOptionEqualToValue={(option, value) =>
                  option.dinas_id === value?.dinas_id
                }
                loading={loadingDinas}
                disabled={loadingDinas}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    placeholder="Pilih Dinas"
                    variant="outlined"
                    fullWidth
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.dinas_id}>
                    {option.nama_dinas}
                  </li>
                )}
              />
            </MDBox>
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormField
              type="select"
              label={pangkat.label}
              name={pangkat.name}
              value={pangkatV}
              options={[
                { value: "", label: "Pilih Pangkat/Golongan" },
                { value: "IA", label: "Juru Muda (I/a)" },
                { value: "IB", label: "Juru Muda Tingkat I (I/b)" },
                { value: "IC", label: "Juru (I/c)" },
                { value: "ID", label: "Juru Tingkat I (I/d)" },
                { value: "IIA", label: "Pengatur Muda (II/a)" },
                { value: "IIB", label: "Pengatur Muda Tingkat I (II/b)" },
                { value: "IIC", label: "Pengatur (II/c)" },
                { value: "IID", label: "Pengatur Tingkat I (II/d)" },
                { value: "IIIA", label: "Penata Muda (III/a)" },
                { value: "IIIB", label: "Penata Muda Tingkat I (III/b)" },
                { value: "IIIC", label: "Penata (III/c)" },
                { value: "IIID", label: "Penata Tingkat I (III/d)" },
                { value: "IVA", label: "Pembina (IV/a)" },
                { value: "IVB", label: "Pembina Tingkat I (IV/b)" },
                { value: "IVC", label: "Pembina Utama Muda (IV/c)" },
                { value: "IVD", label: "Pembina Utama Madya (IV/d)" },
                { value: "P3K", label: "P3K" },
              ]}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormField
              type="select"
              label={bidang.label}
              name={bidang.name}
              value={bidangV}
              options={bidangOptions}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormField
              type="select"
              label={jabatan.label}
              name={jabatan.name}
              value={jabatanV}
              options={jabatanOptions}
              onChange={(e) => {
                const selectedJabatan = e.target.value;
                // Cari level_jabatan dari dinasStruktur.struktur
                let level = "";
                if (dinasStruktur && Array.isArray(dinasStruktur.struktur)) {
                  const found = dinasStruktur.struktur.find(
                    (j) => j.nama_jabatan === selectedJabatan
                  );
                  if (found) level = found.level_jabatan;
                }
                // levelJabatanV = level; // HAPUS BARIS INI
                // Update juga di formik jika ingin
                setValues({
                  ...values,
                  jabatan: selectedJabatan,
                  level_jabatan: level,
                });
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormField
              type="select"
              label={unit.label}
              name={unit.name}
              value={unitV}
              options={[
                { value: "", label: "Pilih Unit" },
                { value: "Sekretariat", label: "Sekretariat" },
                { value: "Sub Bagian", label: "Sub Bagian" },
                { value: "Bidang", label: "Bidang" },
                { value: "Seksi", label: "Seksi" },
                { value: "UPT", label: "UPT" },
                { value: "Lainnya", label: "Lainnya" },
              ]}
            />
          </Grid>
        </Grid>
      </MDBox>
    </MDBox>
  );
}

// typechecking props for Address
InfoUser.propTypes = {
  formData: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
};

export default InfoUser;
